import { useEffect, useState } from 'react';
import {
  Check,
  Monitor,
  Moon,
  Sun,
  type LucideIcon,
} from 'lucide-react';
import { useTheme } from '@/app/providers';
import {
  SettingsPageHeader,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';

type ThemeChoiceValue = 'light' | 'dark' | 'system';

type ThemeChoice = {
  value: ThemeChoiceValue;
  label: string;
  description: string;
  Icon: LucideIcon;
};

const choices: ThemeChoice[] = [
  {
    value: 'light',
    label: 'Light',
    description: 'Bright surfaces, dark text.',
    Icon: Sun,
  },
  {
    value: 'dark',
    label: 'Dark',
    description: 'Dim surfaces, light text.',
    Icon: Moon,
  },
  {
    value: 'system',
    label: 'System',
    description: 'Match your OS setting.',
    Icon: Monitor,
  },
];

type DiffTheme = 'default' | 'colorblind' | 'cursor';

const DIFF_THEME_OPTIONS: DiffTheme[] = ['default', 'colorblind', 'cursor'];

const DIFF_THEME_LABELS: Record<DiffTheme, string> = {
  default: 'Default',
  colorblind: 'Colorblind-safe',
  cursor: 'High contrast',
};

const DIFF_THEME_DESCRIPTIONS: Record<DiffTheme, string> = {
  default: 'Green insert, rose delete.',
  colorblind: 'Blue insert, amber delete.',
  cursor: 'Emerald insert, red delete.',
};

export function AppearanceScene() {
  const { theme, setTheme } = useTheme();
  const [themeChoice, setThemeChoice] = useState<ThemeChoiceValue>('light');
  const [systemNote, setSystemNote] = useState(false);
  const [diffTheme, setDiffTheme] = useState<DiffTheme>('default');
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!mounted) return;
    if (themeChoice !== 'system') {
      setThemeChoice(theme);
    }
  }, [mounted, theme, themeChoice]);

  const handleThemeSelect = (value: ThemeChoiceValue) => {
    setThemeChoice(value);
    if (value === 'system') {
      setTheme('light');
      setSystemNote(true);
    } else {
      setTheme(value);
      setSystemNote(false);
    }
  };

  return (
    <SurfacePageLayout width="narrow">
      <Commentable name="AppearanceScene">
        <div className="contents">
          <SurfacePageBody className="space-y-8" as="div">
          <SettingsPageHeader
            title="Appearance"
            description="Choose how the interface looks."
          />

          <Commentable name="ThemeCards">
            <div>
              {systemNote ? (
                <p className="mb-3 text-sm text-muted">
                  System preference is not wired in this mock — applied Light theme.
                </p>
              ) : null}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {choices.map((choice) => (
                  <ThemeCard
                    key={choice.value}
                    choice={choice}
                    active={mounted && themeChoice === choice.value}
                    onSelect={() => handleThemeSelect(choice.value)}
                  />
                ))}
              </div>
            </div>
          </Commentable>

          <Commentable name="DiffThemeCards">
            <section className="flex flex-col gap-4">
              <div>
                <h2 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">
                  Diff theme
                </h2>
                <p className="mt-1 text-sm text-muted">
                  Colors for insert and delete highlights in the document editor.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {DIFF_THEME_OPTIONS.map((preset) => (
                  <DiffThemeCard
                    key={preset}
                    preset={preset}
                    active={mounted && diffTheme === preset}
                    onSelect={() => setDiffTheme(preset)}
                  />
                ))}
              </div>
            </section>
          </Commentable>
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

function DiffThemeCard({
  preset,
  active,
  onSelect,
}: {
  preset: DiffTheme;
  active: boolean;
  onSelect: () => void;
}) {
  const label = DIFF_THEME_LABELS[preset];
  const description = DIFF_THEME_DESCRIPTIONS[preset];

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={
        active
          ? 'group flex flex-col gap-3 rounded-2xl border-2 border-accent-500 bg-[var(--glass-surface)] p-4 text-left backdrop-blur-lg transition-colors'
          : 'group flex flex-col gap-3 rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 text-left backdrop-blur-lg transition-colors hover:border-[var(--glass-border-strong)]'
      }
    >
      <DiffThemePreview preset={preset} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-zinc-900 dark:text-zinc-100">
            {label}
          </span>
          {active ? (
            <Check className="h-3.5 w-3.5 text-zinc-700 dark:text-zinc-200" />
          ) : null}
        </div>
        <p className="mt-0.5 text-xs text-muted">{description}</p>
      </div>
    </button>
  );
}

function DiffThemePreview({ preset }: { preset: DiffTheme }) {
  const insert =
    preset === 'colorblind'
      ? 'bg-sky-200'
      : preset === 'cursor'
        ? 'bg-emerald-200'
        : 'bg-emerald-100';
  const del =
    preset === 'colorblind'
      ? 'bg-amber-200 line-through'
      : preset === 'cursor'
        ? 'bg-red-200 line-through'
        : 'bg-rose-100 line-through';

  return (
    <div className="flex h-24 flex-col justify-center gap-1.5 rounded-lg border border-divider bg-zinc-50 p-3 dark:bg-zinc-900">
      <div className={cn('rounded px-2 py-0.5 text-xs text-zinc-800', insert)}>
        inserted text
      </div>
      <div className={cn('rounded px-2 py-0.5 text-xs text-zinc-800', del)}>
        deleted text
      </div>
    </div>
  );
}

function ThemeCard({
  choice,
  active,
  onSelect,
}: {
  choice: ThemeChoice;
  active: boolean;
  onSelect: () => void;
}) {
  const { value, label, description, Icon } = choice;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={
        active
          ? 'group flex flex-col gap-3 rounded-2xl border-2 border-accent-500 bg-[var(--glass-surface)] p-4 text-left backdrop-blur-lg transition-colors'
          : 'group flex flex-col gap-3 rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 text-left backdrop-blur-lg transition-colors hover:border-[var(--glass-border-strong)]'
      }
    >
      <ThemePreview variant={value} />
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-zinc-900 dark:text-zinc-100">
              {label}
            </span>
            {active ? (
              <Check className="h-3.5 w-3.5 text-zinc-700 dark:text-zinc-200" />
            ) : null}
          </div>
          <p className="mt-0.5 text-xs text-muted">{description}</p>
        </div>
      </div>
    </button>
  );
}

function ThemePreview({ variant }: { variant: ThemeChoiceValue }) {
  if (variant === 'system') {
    return (
      <div className="relative h-24 overflow-hidden rounded-lg border border-divider">
        <div className="absolute inset-0 grid grid-cols-2">
          <MiniMock tone="light" />
          <MiniMock tone="dark" />
        </div>
      </div>
    );
  }
  return (
    <div className="h-24 overflow-hidden rounded-lg border border-divider">
      <MiniMock tone={variant} />
    </div>
  );
}

function MiniMock({ tone }: { tone: 'light' | 'dark' }) {
  const isDark = tone === 'dark';
  const bg = isDark ? 'bg-zinc-900' : 'bg-zinc-50';
  const card = isDark ? 'bg-zinc-800' : 'bg-white';
  const line = isDark ? 'bg-zinc-700' : 'bg-zinc-200';
  const accent = isDark ? 'bg-zinc-600' : 'bg-zinc-300';

  return (
    <div className={cn('flex h-full w-full flex-col gap-1.5 p-2', bg)}>
      <div className={cn('h-2 w-1/2 rounded-full', accent)} />
      <div className={cn('flex-1 rounded-md p-1.5', card)}>
        <div className={cn('h-1.5 w-3/4 rounded-full', line)} />
        <div className={cn('mt-1 h-1.5 w-1/2 rounded-full', line)} />
      </div>
    </div>
  );
}
